import { useCallback, useMemo, useState } from 'react'
import { FetchState } from 'shared/api'
import { flatten, maxText } from 'shared/util'
import { useKeyBind } from 'shared/keyBind'

import { TrialResult, useGetTrial } from 'interface/trial'

import { useSearch, useTargetList } from 'features/search/context'
import { useSisetuPreviewDialog } from 'features/sisetu/dialog/preview/hooks'
import { useKojinPreviewDialog } from 'features/kojin/dialog/preview/hooks'
import { SisetuResult } from 'interface/sisetu'
import { KojinResult } from 'interface/kojin'

type useTrialPreviewProps = {
  trialList?: TrialResult[]
  defaultTrialId?: number | null
	defaultTrial?: TrialResult | null
  targetSisetuCode?: string
  targetKojinCode?: string
	onSisetuOpenReply?: (sisetuCode: string | null) => void
  onKojinOpenReply?: (kojinCode: string | null) => void
  onClose?: () => void
}
export const useTrialPreview = ({
	trialList,
	defaultTrialId,
	defaultTrial,
  targetSisetuCode,
  targetKojinCode,
	onSisetuOpenReply,
	onKojinOpenReply,
  onClose
}: useTrialPreviewProps) => {
	const search = useSearch()
	const targetList = useTargetList()

	const [trialId, setTrialId] = useState<number | null>(defaultTrialId ?? defaultTrial?.trialId ?? null)

	const {
		current,
		prev,
		next,
	} = useMemo(
		() => {
			const index = (
				trialId ? (trialList?.findIndex(trial => trial.trialId === trialId) ?? -1) :
				-1
			)
			return {
				current: (trialList && index >= 0) ? trialList[index] : null,
				next: (trialList && index >= 0 && index < (trialList.length - 1)) ? trialList[index + 1] : null,
				prev: (trialList && index > 0) ? trialList[index - 1] : null
			}
		},
		[trialList, trialId]
	)

	const trialState: FetchState<TrialResult> = useGetTrial(
		search?.searchId,
		trialId,
		{
			defaultTrial: current ?? defaultTrial ?? undefined,
			updateTime: maxText(targetList?.updateTime, search?.updateTime) ?? undefined
		}
	)
  const trial = useMemo(() => trialState.data, [trialState])

	const {
		setSisetuList, sisetuList,
		openSisetu, sisetuCode: selectedSisetuCode
	} = useSisetuPreviewDialog()
	const {
		setKojinList, kojinList,
		openKojin, kojinCode: selectedKojinCode
	} = useKojinPreviewDialog()

	const isShowModal = useMemo(
		() => !!(selectedSisetuCode || selectedKojinCode),
		[selectedSisetuCode, selectedKojinCode]
	)

	const openPrev = useCallback(
		() => {
			if (isShowModal) return
			if (prev?.trialId) setTrialId(prev.trialId)
		},
		[prev, setTrialId, isShowModal]
	)
	const openNext = useCallback(
		() => {
			if (isShowModal) return
			if (next?.trialId) setTrialId(next.trialId)
		},
		[next, setTrialId, isShowModal]
	)

	const closeModal = useCallback(
		(exclusive: 'sisetu' | 'kojin' | null = null) => {
			if (exclusive !== 'sisetu') {
				if (onSisetuOpenReply) {
					onSisetuOpenReply(null)
				} else {
					openSisetu(null)
				}
			}
			if (exclusive !== 'kojin') {
				if (onKojinOpenReply) {
					onKojinOpenReply(null)
				} else {
					openKojin(null)
				}
			}
		},
		[onSisetuOpenReply, openSisetu, onKojinOpenReply, openKojin]
	)

	const onSisetuOpen = useCallback(
		(sisetuCode: string | null, list?: SisetuResult[]) => {
			closeModal('sisetu')
			if (onSisetuOpenReply) {
				onSisetuOpenReply(sisetuCode)
			} else {
				setSisetuList(list ?? [])
				openSisetu(sisetuCode)
			}
		},
		[closeModal, onSisetuOpenReply, openSisetu, setSisetuList]
	)

	const onKojinOpen = useCallback(
		(kojinCode: string | null, list?: KojinResult[]) => {
			closeModal('kojin')
			if (onKojinOpenReply) {
				onKojinOpenReply(kojinCode)
			} else {
				setKojinList(list ?? [])
				openKojin(kojinCode)
			}
		},
		[closeModal, onKojinOpenReply, openKojin, setKojinList]
	)

  useKeyBind({ key: "PageUp", onKeyDown: openPrev })
  useKeyBind({ key: "PageDown", onKeyDown: openNext })

	return {
		loading: trialState.loading,
		error: flatten([trialState.error]),
		trialList, trialId, trial, 
		targetSisetuCode,
		targetKojinCode,
		openPrev, hasPrev: prev !== null ? true : false,
		openNext, hasNext: next !== null ? true : false,
		onSisetuOpen, selectedSisetuCode, sisetuList,
		onKojinOpen, selectedKojinCode, kojinList,
		closeModal,
		onClose
	}
}
